import { useDispatch, useSelector } from 'react-redux';
import AppRoutes from './routes/AppRoutes';
import { useNavigate, useLocation } from 'react-router-dom';
import { AppDispatch, RootState } from './redux-stores/store';
import { useEffect, useState } from 'react';
import useCheckVersion from './hooks/useCheckVersion';
import { APP_ENV, WEB_APP_URL } from './constants/constants';
import { initAuth } from './redux-stores/slices/authSlice';
import Loader from './components/loaders/Loader';
import { StatusBar, Style } from "@capacitor/status-bar";
import { useMenuScrollLock } from './hooks/useMenuScrollLock';
import { usePullToRefresh } from './hooks/usePullToRefresh';
import { initPushListeners } from './lib/pushNotifications';
import { Keyboard, KeyboardResize } from '@capacitor/keyboard';

import { initDeviceDetection } from './utils/deviceDetection';
import withSlideUpModal from './utils/hoc/withSlideUpModalComponent';
import UpdateRequired from './components/UpdateRequired';
import { checkLatestAppVersion } from './services/api/device';
import { App as CapacitorApp } from '@capacitor/app';
import { Device } from '@capacitor/device';
import { analyticsLog, initAnalytics } from "./lib/analytics";
import { isNativePlatform } from './utils/platformUtil';
import { initAdsTracking } from './utils/adsTracking';
const UpdateRequiredPopup = withSlideUpModal(UpdateRequired);

function DeepLinkListener() {
    const navigate = useNavigate();

    useEffect(() => {
        const sub = CapacitorApp.addListener('appUrlOpen', ({ url }) => {
            try {
                const parsedUrl = new URL(url); 

                const path = url.startsWith("influns://") ? url.replace('influns:/', '') : parsedUrl.pathname+parsedUrl.search;

                if (path) {
                    navigate(path, { replace: true });
                }
            } catch (err) {
                console.error('Deep link handling failed:', err);
            }
        });

        return () => {
            sub.then(listener => listener.remove());
        };
    }, [navigate]);

    return null;
}

function PushNotificationListener() {
    const navigate = useNavigate();

    useEffect(() => {
        initPushListeners(
            (data) => {
                if (data?.route) {
                    console.log('Push notification redirecting to:' + data.route);
                    let path = data.route;
                    try {
                        const url = new URL(data.route);
                        path = url.pathname + url.search + url.hash;
                    } catch (e) {

                    }
                    setTimeout(() => {navigate(path);}, 0);
                }
            },
            (notification) => {
                console.log('Foreground push received:', notification);
            }
        );
    }, [navigate]);

    return null;
}
const RouteTracker = () => {
    const location = useLocation();
    useEffect(() => {
        if(APP_ENV !== 'production') return;
        
        analyticsLog('page_view', { 
            page_path: location.pathname
        })
    }, [location]);
    return null;
};

function App() {
    const mode = useSelector((state: RootState) => state.theme.mode);
    const authInitialized = useSelector((state: RootState) => state.auth.authInitialized);
    const dispatch = useDispatch<AppDispatch>();
    const [updateRequiredPopup, showUpdateRequiredPopup] = useState<any>(null);

    useCheckVersion();
    useMenuScrollLock();
    usePullToRefresh();

    useEffect(() => {
        document.body.classList.toggle('theme-dark', mode === 'dark');
        document.body.classList.toggle('theme-light', mode === 'light');
    }, [mode]);

    useEffect(() => {
        dispatch(initAuth());
    }, [dispatch]);

    useEffect(() => {
        const initStatusBar = async () => {
            try {
                await StatusBar.setStyle({ style: Style.Light });
                await StatusBar.setBackgroundColor({ color: '#ffffff' });
                await StatusBar.setOverlaysWebView({ overlay: false });
            } catch (err) {
                console.warn('StatusBar not available:', err);
            }
        };
        if(isNativePlatform()){
            initStatusBar();
        }
    }, []);

    useEffect(() => {
        if (isNativePlatform()) {
            Keyboard.setResizeMode({ mode: KeyboardResize.Native });
        }
    }, []);

    useEffect(() => {
        initAdsTracking();
        initDeviceDetection();
        initAnalytics();
    }, []);

    useEffect(() => {
        const checkDeviceAppVersion = async () => {
            if (!isNativePlatform()) return;
            try {
                const info = await Device.getInfo();
                const appInfo = await CapacitorApp.getInfo();
                const res = await checkLatestAppVersion(info.platform, appInfo.version);
                if (res.hard_update) {
                    showUpdateRequiredPopup({
                        latest_version: res.latest_version!,
                        store_url: res.store_url ?? WEB_APP_URL
                    });
                }
            } catch (error) {
                console.error('App version check failed', error);
            }
        };

        checkDeviceAppVersion();
    }, []);

    useEffect(() => {
        if (!("serviceWorker" in navigator)) return;

        navigator.serviceWorker.getRegistrations().then(registrations => {
            registrations.forEach(reg => reg.unregister());
        });

        // Important: clear PWA caches too
        if ("caches" in window) {
            caches.keys().then(keys => {
            keys.forEach(key => caches.delete(key));
            });
        }
    }, []);


    if (!authInitialized) {
        return <Loader isLoading={true} />;
    }
    

    return (
        <div id="page">
            <DeepLinkListener />
            <RouteTracker />
            <PushNotificationListener />

            {authInitialized ? (
                <AppRoutes />
            ) : (
                <Loader isLoading={true} />
            )}

            <UpdateRequiredPopup
                isActive={!!updateRequiredPopup}
                onClose={() => {}}
                {...updateRequiredPopup}
            />
        </div>
    );
}

export default App;
